import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    Shield,
    Users,
    Mail,
    DollarSign,
    ArrowRight,
    Check,
    Zap,
    Lock,
    Eye,
    Car,
    Home,
    Plane,
    ShoppingBag,
    Ship,
    DoorOpen,
    Calendar,
    Hash,
    Code,
    Clock,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LandingPage() {
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (user) {
            if (user.account_type === "executive") navigate("/dashboard/executive", { replace: true });
            else if (user.account_type === "business") navigate("/dashboard/business", { replace: true });
            else navigate("/onboarding", { replace: true });
        }
    }, [user, navigate]);

    const handleSignIn = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + '/onboarding';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const handleExecutiveSignIn = () => {
        // Executives go directly to Outlook OAuth — one step for sign-in + inbox access
        const origin = window.location.origin;
        window.location.href = `${BACKEND_URL}/api/auth/outlook?origin=${encodeURIComponent(origin)}`;
    };

    const handleBusinessSignIn = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + '/onboarding';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-screen-safe bg-[#050505] relative overflow-hidden">
            {/* Hero */}
            <section className="relative min-h-screen flex items-center" style={{ background: "radial-gradient(circle at 30% 50%, rgba(4,120,87,0.12) 0%, rgba(5,5,5,0) 60%)" }}>
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
                    <div className="text-center max-w-4xl mx-auto space-y-8">
                        <div className="fade-in-up">
                            <Badge variant="outline" className="border-[#047857]/30 text-[#047857] px-4 py-1.5 text-sm tracking-[0.2em] uppercase" data-testid="hero-badge">
                                Consent Commerce
                            </Badge>
                        </div>

                        <h1 className="fade-in-up fade-in-up-delay-1 text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1]" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="hero-title">
                            <span className="italic">The Irish</span>
                            <br />
                            <span className="gold-shimmer">Goodbye</span>
                        </h1>

                        <p className="fade-in-up fade-in-up-delay-2 text-base sm:text-lg leading-relaxed text-[#9CA3AF] max-w-2xl mx-auto" data-testid="hero-description">
                            The polite exit from the noise. Your attention, your terms.
                        </p>

                        {/* Dual CTA */}
                        <div className="fade-in-up fade-in-up-delay-3 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto pt-4">
                            <Card className="bg-[#0a0a0a] border-white/10 hover:border-[#047857]/30 transition-all duration-500 cursor-pointer group" onClick={handleExecutiveSignIn} data-testid="cta-executive">
                                <CardContent className="p-8 text-center space-y-4">
                                    <div className="w-14 h-14 rounded-full bg-[#047857]/10 flex items-center justify-center mx-auto group-hover:bg-[#047857]/20 transition-colors">
                                        <Shield className="h-7 w-7 text-[#047857]" />
                                    </div>
                                    <h3 className="text-xl font-semibold" style={{ fontFamily: "Manrope, sans-serif" }}>I'm an Executive</h3>
                                    <p className="text-sm text-[#9CA3AF] leading-relaxed">
                                        Shield your inbox from sales noise. Set your price for a reply. Make prospectors earn your attention.
                                    </p>
                                    <Button className="w-full bg-[#047857] text-white hover:bg-[#036046] shadow-[0_0_20px_rgba(4,120,87,0.5)] transition-all" data-testid="cta-executive-button">
                                        <Mail className="mr-2 h-4 w-4" />
                                        Protect My Inbox
                                        <ArrowRight className="ml-2 h-4 w-4" />
                                    </Button>
                                </CardContent>
                            </Card>

                            <Card className="bg-[#0a0a0a] border-white/10 hover:border-[#D4AF37]/30 transition-all duration-500 cursor-pointer group" onClick={handleBusinessSignIn} data-testid="cta-business">
                                <CardContent className="p-8 text-center space-y-4">
                                    <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto group-hover:bg-[#D4AF37]/20 transition-colors">
                                        <Users className="h-7 w-7 text-[#D4AF37]" />
                                    </div>
                                    <h3 className="text-xl font-semibold" style={{ fontFamily: "Manrope, sans-serif" }}>I'm a Business</h3>
                                    <p className="text-sm text-[#9CA3AF] leading-relaxed">
                                        Convert anonymous visitors into verified leads. Gate your best prices behind identity and consent.
                                    </p>
                                    <Button className="w-full bg-[#047857] text-white hover:bg-[#036046] shadow-[0_0_20px_rgba(4,120,87,0.5)] transition-all" data-testid="cta-business-button">
                                        <Lock className="mr-2 h-4 w-4" />
                                        Get Verified Leads
                                        <ArrowRight className="ml-2 h-4 w-4" />
                                    </Button>
                                </CardContent>
                            </Card>
                        </div>

                        <p className="fade-in-up fade-in-up-delay-4 text-sm text-[#6B7280] font-mono">
                            Slip out gracefully.
                        </p>
                    </div>
                </div>
            </section>

            {/* Executive Features */}
            <section className="py-24 md:py-32 px-6 md:px-12 border-t border-white/5" id="executive">
                <div className="max-w-7xl mx-auto">
                    <div className="mb-16">
                        <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#047857] mb-4">For Executives</p>
                        <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                            Inbox <span className="italic">Shield</span>
                        </h2>
                        <p className="text-[#9CA3AF] mt-4 max-w-xl">AI-powered protection from the endless wave of sales outreach. Your attention has a price — set it.</p>
                        <Badge variant="outline" className="border-[#047857]/30 text-[#047857] mt-3">Outlook supported — Gmail coming soon</Badge>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <FeatureCard icon={Eye} title="AI Detection" description="Automatically classify every incoming email: prospecting, follow-up, cold outreach, legitimate, or personal." accent="green" />
                        <FeatureCard icon={DollarSign} title="Pay to Reply" description="Set your price. $25, $50, $100, $250 — salespeople pay to earn your thoughtful response." accent="gold" />
                        <FeatureCard icon={Shield} title="Auto-Shield" description="Detected sales emails get an automatic response with your pricing policy and payment link." accent="green" />
                        <FeatureCard icon={DoorOpen} title="Thread Exit" description="Irish Goodbye or Ghost your way off email threads. One click — you're out, the team carries on." accent="green" />
                        <FeatureCard icon={Calendar} title="Calendar Guard" description="Control who books your time. Meeting-free days, buffers between meetings, auto-decline rules." accent="gold" />
                        <FeatureCard icon={Zap} title="Whitelist" description="Trusted domains and contacts bypass the shield entirely. Your team, your clients — always through." accent="green" />
                        <FeatureCard icon={Hash} title="Slack Channels" description="Drop off Slack channels too. Irish Goodbye or Ghost — same clean exit, different platform." accent="green" />
                        <FeatureCard icon={Mail} title="Public Policy Page" description="irishgoodbye.io/u/yourname — prospectors can check your rates before they even email you." accent="gold" />
                        <FeatureCard icon={Check} title="Sender Ratings" description="Rate the quality of paid interactions. Build a reputation score for senders over time." accent="green" />
                    </div>
                </div>
            </section>

            {/* Business Features */}
            <section className="py-24 md:py-32 px-6 md:px-12 border-t border-white/5" id="business">
                <div className="max-w-7xl mx-auto">
                    <div className="mb-16">
                        <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#D4AF37] mb-4">For Businesses</p>
                        <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                            Consent <span className="italic">Commerce</span>
                        </h2>
                        <p className="text-[#9CA3AF] mt-4 max-w-xl">Turn anonymous browsers into verified, opted-in leads. Gate your best prices behind real identity.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <FeatureCard icon={Lock} title="Tiered Unlocks" description="Tier 1: email for info. Tier 2: name + phone for real price. Tier 3: full qualification for best deal." accent="gold" />
                        <FeatureCard icon={Users} title="Verified Leads" description="Every lead is verified, opted-in, and consented — with an auditable receipt and expiry date." accent="gold" />
                        <FeatureCard icon={Shield} title="AI Agent Gate" description="Detect AI shopping agents. No identity token? They see list price only. Humans get the real deal." accent="green" />
                        <FeatureCard icon={Code} title="One-Line Embed" description="Add one script tag to your website. Works with Shopify, WordPress, Squarespace, custom sites — any HTML page." accent="gold" />
                        <FeatureCard icon={DollarSign} title="Discount Rules" description="Set exactly what consumers unlock at each tier. Fixed discounts, percentages, free shipping — fully configurable." accent="gold" />
                        <FeatureCard icon={Zap} title="Industry Templates" description="Pre-built templates for auto, real estate, travel, boats, and e-commerce. One click to configure." accent="green" />
                        <FeatureCard icon={Clock} title="Consent Renewals" description="Leads expiring? Send a renewal offer — $10 off an oil change, a free consultation — and they re-opt in. Stay compliant in California and beyond." className="md:col-span-2 lg:col-span-3" accent="gold" />
                    </div>

                    <div className="mt-12 flex flex-wrap gap-4 justify-center">
                        {[
                            { icon: Car, label: "Automotive" },
                            { icon: Home, label: "Real Estate" },
                            { icon: Plane, label: "Travel" },
                            { icon: Ship, label: "Boats" },
                            { icon: ShoppingBag, label: "E-Commerce" },
                        ].map((v) => (
                            <Badge key={v.label} variant="outline" className="border-white/10 text-[#9CA3AF] px-4 py-2 text-sm gap-2">
                                <v.icon className="h-4 w-4" />{v.label}
                            </Badge>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 md:py-32 px-6 md:px-12 border-t border-white/5">
                <div className="max-w-2xl mx-auto text-center space-y-6">
                    <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                        Ready to <span className="gold-shimmer">slip out</span>?
                    </h2>
                    <p className="text-[#9CA3AF]">Whether you're an executive drowning in sales emails or a business losing leads to anonymous browsers — Irish Goodbye is your consent layer.</p>
                    <Button onClick={handleSignIn} className="bg-[#047857] text-white hover:bg-[#036046] shadow-[0_0_20px_rgba(4,120,87,0.5)] px-8 py-6 text-lg font-serif italic" data-testid="cta-bottom">
                        Get Started <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-6 md:px-12 border-t border-white/5">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-2">
                        <Shield className="h-5 w-5 text-[#047857]" />
                        <span className="font-serif italic text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>The Irish Goodbye</span>
                    </div>
                    <div className="flex items-center gap-6">
                        <Link to="/pricing" className="text-xs text-[#6B7280] hover:text-[#9CA3AF] transition-colors" data-testid="footer-pricing">Pricing</Link>
                        <Link to="/terms" className="text-xs text-[#6B7280] hover:text-[#9CA3AF] transition-colors" data-testid="footer-terms">Terms</Link>
                        <Link to="/privacy" className="text-xs text-[#6B7280] hover:text-[#9CA3AF] transition-colors" data-testid="footer-privacy">Privacy</Link>
                        <Link to="/vault" className="text-xs text-[#6B7280] hover:text-[#9CA3AF] transition-colors" data-testid="footer-vault">Consent Vault</Link>
                    </div>
                    <p className="text-xs text-[#6B7280]/60">&copy; 2026 The Irish Goodbye</p>
                </div>
            </footer>
        </div>
    );
}

function FeatureCard({ icon: Icon, title, description, accent = "green", className = "" }) {
    const accentColor = accent === "gold" ? "#D4AF37" : "#047857";
    return (
        <Card className={`bg-[#0a0a0a] border-white/5 hover:border-white/20 transition-all duration-500 hover:-translate-y-1 ${className}`}>
            <CardContent className="p-6 space-y-3">
                <div className="w-10 h-10 rounded-lg border border-white/10 flex items-center justify-center" style={{ color: accentColor }}>
                    <Icon className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold" style={{ fontFamily: "Manrope, sans-serif" }}>{title}</h3>
                <p className="text-sm text-[#9CA3AF] leading-relaxed">{description}</p>
            </CardContent>
        </Card>
    );
}
